import { useEffect, useMemo, useState } from "react";
import { Alert, Card, Col, Row, Spinner } from "react-bootstrap";
import { listMissionSupporters } from "../services/api";

const supporterTabs = [
  {
    key: "heroes",
    label: "Heroes Helping Heroes",
    eyebrow: "Community Supporters",
    title: "Honoring Those Who Give From the Heart",
    description:
      "Heroes Helping Heroes recognizes individuals, families, neighbors, and community members who step forward with generosity to support local veterans.",
    highlights: [
      "Every act of support helps local veterans feel seen, valued, and connected.",
      "Community generosity can provide encouragement, practical help, and hope when it is needed most.",
      "These heroes strengthen the Fremont Veterans Group mission by standing beside those who served.",
    ],
  },
  {
    key: "partners",
    label: "Patriot Business Partners",
    eyebrow: "Business Allies",
    title: "Recognizing Businesses That Stand Beside Our Mission",
    description:
      "Patriot Business Partners highlights local businesses and organizations that help Fremont Veterans Group serve veterans in need.",
    highlights: [
      "Local businesses help extend care, resources, and awareness throughout the community.",
      "Partnerships create stronger pathways for veterans and families to find meaningful support.",
      "Together, these allies help build a polished, trusted network around the Fremont Veterans Group mission.",
    ],
  },
];

function MissionSupportersPage() {
  const [activeTab, setActiveTab] = useState(supporterTabs[0].key);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const activeSupporterTab = supporterTabs.find((tab) => tab.key === activeTab) || supporterTabs[0];

  useEffect(() => {
    let mounted = true;
    listMissionSupporters()
      .then((response) => {
        if (!mounted) return;
        setSupporters(response.data || []);
        setError("");
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || "Failed to load mission supporters.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const activeSupporters = useMemo(
    () => supporters.filter((supporter) => String(supporter.supporterSection || "") === activeTab),
    [supporters, activeTab],
  );

  return (
    <section>
      <Card className="glass-card text-light mb-4 border-warning">
        <Card.Body>
          <p className="hero-kicker mb-2">Mission Supporters</p>
          <h1 className="section-title mb-3">Mission Supporters</h1>
          <p className="text-muted-soft lead mb-0">
            A dedicated place to honor the people, families, businesses, and community partners helping Fremont Veterans Group support local veterans.
          </p>
        </Card.Body>
      </Card>

      <Card className="glass-card text-light mission-supporters-card">
        <Card.Body>
          <div className="mission-supporters-tabs" role="tablist" aria-label="Mission supporters views">
            {supporterTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`mission-supporters-tab ${activeTab === tab.key ? "is-active" : ""}`}
                role="tab"
                aria-selected={activeTab === tab.key}
                aria-controls={`mission-supporters-panel-${tab.key}`}
                id={`mission-supporters-tab-${tab.key}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div
            className="mission-supporters-panel mt-4"
            role="tabpanel"
            id={`mission-supporters-panel-${activeSupporterTab.key}`}
            aria-labelledby={`mission-supporters-tab-${activeSupporterTab.key}`}
          >
            <Row className="g-4 align-items-stretch mb-4">
              <Col lg={5}>
                <Card className="resource-card text-light h-100 mission-supporters-feature-card">
                  <Card.Body>
                    <p className="hero-kicker mb-2">{activeSupporterTab.eyebrow}</p>
                    <h2 className="h3 mb-3">{activeSupporterTab.label}</h2>
                    <p className="text-muted-soft mb-0">{activeSupporterTab.description}</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={7}>
                <Card className="resource-card text-light h-100 mission-supporters-impact-card">
                  <Card.Body>
                    <h3 className="h4 mb-3">{activeSupporterTab.title}</h3>
                    <div className="mission-supporters-highlight-grid">
                      {activeSupporterTab.highlights.map((highlight, index) => (
                        <div className="mission-supporters-highlight" key={`${activeSupporterTab.key}-highlight-${index}`}>
                          <span className="mission-supporters-highlight-icon" aria-hidden="true">★</span>
                          <p className="text-muted-soft mb-0">{highlight}</p>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {loading && <div className="text-center py-4"><Spinner animation="border" variant="warning" /></div>}
            {error && <Alert variant="warning">{error}</Alert>}
            {!loading && !error && activeSupporters.length > 0 && (
              <Row className="g-3">
                {activeSupporters.map((supporter) => (
                  <Col md={6} xl={4} key={`mission-supporter-${supporter.id}`}>
                    <Card className="resource-card text-light h-100 mission-supporter-profile-card">
                      <Card.Body>
                        <div className="mission-supporter-medallion" aria-hidden="true">★</div>
                        <h3 className="h5 mb-2">{supporter.supporterName}</h3>
                        {supporter.description ? (
                          <p className="text-muted-soft mb-0">{supporter.description}</p>
                        ) : (
                          <p className="text-muted-soft mb-0">Thank you for standing with local veterans and the Fremont Veterans Group mission.</p>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </div>
        </Card.Body>
      </Card>
    </section>
  );
}

export default MissionSupportersPage;
